import { useEffect, useRef, useState } from 'react';
import { employerName, employerPossessive } from '@/lib/employer';
import { supabase } from '@/integrations/supabase/client';
import { OfferSlot } from '@/components/OfferSlot';
import { type OfferKey } from '@/lib/offers';
import { siteEvents } from '@/lib/siteEvents';
import { ApplyNewsletterOptIn } from './ApplyNewsletterOptIn';

// localStorage flag, mirrors the existing JobApplyCapture component so
// users who have already captured don't see the modal twice across the
// /jobs/:id and old beta route while we transition.
const STORAGE_KEY = 'audit_friendly_email_captured';

export function hasAlreadyCapturedEmail(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function markEmailAsCaptured(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    /* ignore quota / privacy errors */
  }
}

interface JobDetailV2ApplyModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  jobContext: {
    id?: string;
    title: string;
    company: string;
    location?: string;
    isRemote?: boolean;
  };
}

type Seniority = 'junior' | 'senior' | 'manager' | 'director' | 'executive';

const LEVELS: Array<{ value: Seniority; label: string }> = [
  { value: 'junior', label: 'Junior' },
  { value: 'senior', label: 'Senior' },
  { value: 'manager', label: 'Manager' },
  { value: 'director', label: 'Director' },
  { value: 'executive', label: 'Exec / VP' },
];

export function JobDetailV2ApplyModal({
  open,
  onClose,
  onContinue,
  jobContext,
}: JobDetailV2ApplyModalProps) {
  const [email, setEmail] = useState('');
  const [seniority, setSeniority] = useState<Seniority | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  // The after-apply card, job-seeker products only. Split test apply_offer_v3,
  // per visit, every role: arm 0 the Still Remote pass (the only offer drawing
  // clicks in the 7 days to 2026-10-27), arm 1 the Search Sprint. Replaces
  // apply_offer_v2 (Still Remote or Hired vs Sprint) and the Playbook line
  // close roles used to get under the card.
  const applyOffer: { key: OfferKey; variant: string } = siteEvents.arm('apply_offer_v3') === 1
    ? { key: 'search_sprint', variant: 'apply_offer_v3:search_sprint' }
    : { key: 'still_remote', variant: 'apply_offer_v3:still_remote' };

  // Reset state every time the modal opens fresh, and lock body scroll.
  useEffect(() => {
    if (!open) return;
    setEmail('');
    setSeniority(null);
    setSubmitting(false);
    setSuccess(false);

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => emailRef.current?.focus(), 250);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;

    setSubmitting(true);
    try {
      // Same shape as the existing capture-job-applicant invocation.
      // We don't gate the apply redirect on success: even if the function
      // errors, the user should still get to the company's posting.
      await supabase.functions.invoke('capture-job-applicant', {
        body: {
          email,
          seniority: seniority ?? undefined,
          jobId: jobContext.id,
          jobTitle: jobContext.title,
          jobCompany: jobContext.company,
          jobLocation: jobContext.location,
          isRemote: jobContext.isRemote,
        },
      });
    } catch (err) {
      // Swallow, surface a console note for debugging.
      console.error('capture-job-applicant failed', err);
    } finally {
      markEmailAsCaptured();
      setSubmitting(false);
      setSuccess(true);
    }
  };

  const handleContinue = () => {
    onClose();
    onContinue();
  };

  return (
    <div
      className={`modal-backdrop${open ? ' is-open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="jdv2-modal-title"
      onClick={handleBackdrop}
    >
      <div className={`modal-card${success ? ' is-success' : ''}`}>
        <button
          type="button"
          className="modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        <span className="modal-eyebrow">Almost there</span>
        <h2 id="jdv2-modal-title">
          Get matched <span className="accent">while you apply</span>
        </h2>
        <p>
          Drop your email, we'll auto-alert you when similar roles go up. Then
          we'll send you straight to {employerPossessive(jobContext.company)} application page.
          No spam, no friction.
        </p>

        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="modal-field">
            <div className="modal-field__label">
              <label className="modal-field__label-text" htmlFor="jdv2-modal-email">
                Email
              </label>
            </div>
            <input
              ref={emailRef}
              id="jdv2-modal-email"
              type="email"
              required
              className="modal-input"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="modal-field">
            <div className="modal-field__label">
              <span className="modal-field__label-text">Your level</span>
              <span className="modal-field__optional">
                Optional · helps us match better
              </span>
            </div>
            <div className="level-pills" role="radiogroup" aria-label="Your level">
              {LEVELS.map((lv) => {
                const active = seniority === lv.value;
                return (
                  <button
                    key={lv.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`level-pill${active ? ' is-active' : ''}`}
                    onClick={() =>
                      setSeniority((curr) => (curr === lv.value ? null : lv.value))
                    }
                  >
                    {lv.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button type="submit" className="modal-submit" disabled={submitting}>
            {submitting ? 'Setting up your alerts...' : 'Continue to application →'}
          </button>

          <p className="modal-fineprint">
            One-click unsubscribe anytime. We'll never share your email.{' '}
            <a href="/privacy">Privacy</a>
          </p>
        </form>

        <div className="modal-success">
          <div className="modal-success__icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3>You're set up.</h3>
          <p>
            We'll auto-alert you for similar roles and send a magic link to
            manage your alerts. Sending you to {employerName(jobContext.company) ?? 'the application'} now.
          </p>
          <button
            type="button"
            className="modal-submit"
            onClick={handleContinue}
            style={{ display: 'inline-block', padding: '14px 28px', width: 'auto' }}
          >
            Continue to {employerName(jobContext.company) ?? 'the application'} →
          </button>
          {/* One offer, mounted only once the success screen is showing so
              offer_view counts people who actually saw it. */}
          {success && (
            <div style={{ marginTop: 22 }}>
              <OfferSlot
                offerKey={applyOffer.key}
                surface="apply_success"
                variant={applyOffer.variant}
                compact
              />
              {/* LEAD-04: one explicit, unticked newsletter opt-in for the role family (double opt-in). */}
              <ApplyNewsletterOptIn
                email={email}
                jobTitle={jobContext.title}
                jobId={jobContext.id}
                isRemote={jobContext.isRemote}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
